import { useCallback, useState } from "react";
import { Flex, Button, Text, TextField, Box } from "@radix-ui/themes";
import { useMutation } from "react-query";
import { toast } from "sonner";

import { Dialog } from "@/components/shared/dialog";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";

interface CreateEmptySetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  simulatorId?: number;
}

export function CreateEmptySetDialog({
  open,
  onOpenChange,
  onSuccess,
  simulatorId,
}: CreateEmptySetDialogProps) {
  const regressionApi = useRegressionApi();

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const { mutate: createSet, isLoading } = useMutation(
    () =>
      regressionApi.createRegressionSet({
        name,
        description: description.trim() || undefined,
        simulatorId,
      }),
    {
      onSuccess: () => {
        toast.success("Test set created");
        resetForm();
        onSuccess();
      },
      onError: () => {
        toast.error("Failed to create test set");
      },
    }
  );

  const resetForm = useCallback(() => {
    setName("");
    setDescription("");
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onOpenChange(false);
  }, [resetForm, onOpenChange]);

  const handleCreate = useCallback(() => {
    if (!name.trim()) {
      toast.error("Please enter a name for the test set");
      return;
    }
    createSet();
  }, [name, createSet]);

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setName(e.target.value);
    },
    []
  );

  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setDescription(e.target.value);
    },
    []
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="medium">
        <Flex direction="column" gap="4" p="4">
          <Text size="5" weight="medium">
            Create Test Set
          </Text>

          <Text size="2" color="gray">
            Create an empty test set. You can add items manually afterwards.
          </Text>

          <Box>
            <Text size="2" weight="regular" mb="1" as="label">
              Name
            </Text>
            <TextField.Root
              placeholder="Enter test set name"
              value={name}
              onChange={handleNameChange}
            />
          </Box>

          <Box>
            <Text size="2" weight="regular" mb="1" as="label">
              Description (optional)
            </Text>
            <TextField.Root
              placeholder="Enter description"
              value={description}
              onChange={handleDescriptionChange}
            />
          </Box>

          <Flex gap="3" mt="2" justify="end">
            <Button variant="soft" color="gray" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              color="gray"
              highContrast
              onClick={handleCreate}
              disabled={isLoading || !name.trim()}
            >
              {isLoading ? "Creating..." : "Create Test Set"}
            </Button>
          </Flex>
        </Flex>
      </Dialog.Content>
    </Dialog>
  );
}
